import {ReactElement, ReactNode} from "react";
import {SectionContainer} from "./SharedComponents";
import {StyledBoxColumn, StyledBoxRow, StyledHeadingVariant} from "@/shared/StyledComponents";
import {Grid, Typography, useMediaQuery, useTheme} from "@mui/material";
import {AccessTimeOutlined, EmailOutlined, LocationOnOutlined, PhoneOutlined} from "@mui/icons-material";
import {PD_LG, PD_MD, PD_SM, PD_XLG, PD_XLG2} from "@/shared/dimens";
import {AddressSectionData, SiteTemplate} from "../../types";

function InfoRow(props: { icon: ReactNode, children: ReactNode }): ReactElement {
  return <StyledBoxRow style={{alignItems: "flex-start", gap: PD_MD}}>
    <div style={{flexShrink: 0, marginTop: 2, opacity: 0.45}}>
      {props.icon}
    </div>
    {props.children}
  </StyledBoxRow>;
}

export function AddressSection(props: { data: AddressSectionData, template: SiteTemplate }): ReactElement {
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up("md"));

  const addressPane = <StyledBoxColumn style={{flex: 1, minWidth: 280, gap: PD_XLG, justifyContent: "center"}}>
    <StyledBoxColumn style={{gap: PD_LG}}>
      {(props.data.addressLine1 || props.data.addressLine2)
        ? <InfoRow icon={<LocationOnOutlined/>}>
          <StyledBoxColumn style={{gap: PD_SM / 2}}>
            {props.data.addressLine1
              ? <Typography style={{fontWeight: "700"}}>{props.data.addressLine1}</Typography>
              : null}
            {props.data.addressLine2
              ? <Typography style={{opacity: 0.65}}>{props.data.addressLine2}</Typography>
              : null}
          </StyledBoxColumn>
        </InfoRow>
        : null}
      {props.data.phone
        ? <InfoRow icon={<PhoneOutlined/>}>
          <Typography
            component="a"
            href={`tel:${props.data.phone}`}
            style={{textDecoration: "none", color: "inherit"}}>
            {props.data.phone}
          </Typography>
        </InfoRow>
        : null}
      {props.data.email
        ? <InfoRow icon={<EmailOutlined/>}>
          <Typography
            component="a"
            href={`mailto:${props.data.email}`}
            style={{textDecoration: "none", color: "inherit"}}>
            {props.data.email}
          </Typography>
        </InfoRow>
        : null}
      {props.data.openingHours?.length > 0
        ? <InfoRow icon={<AccessTimeOutlined/>}>
          <StyledBoxColumn style={{gap: PD_SM, width: "100%"}}>
            <Grid container>
              {props.data.openingHours.map((row, i) => <>
                  <Grid key={`days-${i}`} xs={12} sm={6}>
                    <Typography style={{opacity: 0.65}}>{row.days}</Typography>
                  </Grid>
                  <Grid key={`hours-${i}`} xs={12} sm={6}>
                    <Typography style={{fontWeight: "700", paddingBottom: PD_MD}}>{row.hours}</Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </StyledBoxColumn>
        </InfoRow>
        : null}
    </StyledBoxColumn>
  </StyledBoxColumn>;

  const mapPane = <div style={{
    flex: 1,
    minWidth: 280,
    height: "100%",
    minHeight: md ? 480 : 300,
    borderRadius: 8,
    overflow: "hidden",
    border: `2px solid ${theme.palette.secondary.main}`,
  }}>
    {props.data.mapEmbedUrl
      ? <iframe
        src={props.data.mapEmbedUrl}
        width="100%"
        height="100%"
        style={{border: 0, display: "block"}}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"/>
      : <div style={{
        width: "100%",
        height: "100%",
        minHeight: md ? 480 : 300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: props.template.themeExtras.highlightColor,
      }}>
        <StyledBoxColumn style={{alignItems: "center", gap: PD_SM, opacity: 0.4}}>
          <LocationOnOutlined style={{fontSize: 48, color: theme.palette.secondary.main}}/>
          <Typography>Map preview</Typography>
        </StyledBoxColumn>
      </div>}
  </div>;

  return <div style={{width: "100%", backgroundColor: "#0D2918"}}>
    <SectionContainer>
      {props.data.title
        ? <StyledHeadingVariant default_={3}>{props.data.title}</StyledHeadingVariant>
        : null}
      <Grid container>
        <Grid xs={12} md={7}>
          <div style={{width: "100%", display: "flex", alignItems: "center", padding: PD_XLG2}}>
            {addressPane}
          </div>
        </Grid>
        <Grid xs={12} md={5}>
          {mapPane}
        </Grid>
      </Grid>
    </SectionContainer>
  </div>;
}
